// src/services/api.js - Servizio API completo con gestione offline
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
    this.offlineStorage = null; // Verrà configurato dal hook offline
  }

  // METODO CRITICO - setOfflineStorage
  setOfflineStorage(offlineStorage) {
    try {
      if (!offlineStorage) {
        console.warn('⚠️ setOfflineStorage chiamato con valore null/undefined');
        this.offlineStorage = null;
        return;
      }

      this.offlineStorage = offlineStorage;
    } catch (error) {
      console.error('❌ Errore in setOfflineStorage:', error);
      this.offlineStorage = null;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Errore API');
      }

      return response.json();
    } catch (error) {
      // Gestione errori di rete per richieste specifiche
      if (!navigator.onLine) {
        return this.handleOfflineRequest(endpoint, options);
      }
      throw error;
    }
  }

  async handleOfflineRequest(endpoint, options) {
    // Gestione richieste GET per dati cachati
    if (options.method === 'GET' || !options.method) {
      if (endpoint.includes('/customers') && this.offlineStorage) {
        return this.offlineStorage.getCachedCustomers();
      }
      if (endpoint.includes('/products') && this.offlineStorage) {
        return this.offlineStorage.getCachedProducts();
      }
    }

    throw new Error('Connessione non disponibile e nessun dato cache trovato');
  }

  // AUTH METHODS
  async login(email, password) {
    const result = await this.request('/auth/login', {
      method: 'POST',
      body: { email, password },
    });

    this.token = result.token;
    localStorage.setItem('token', this.token);
    return result;
  }

  async register(name, email, password) {
    const result = await this.request('/auth/register', {
      method: 'POST',
      body: { name, email, password },
    });

    this.token = result.token;
    localStorage.setItem('token', this.token);
    return result;
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: {
        currentPassword,
        newPassword,
      },
    });
  }

  logout() {
    this.token = null;
    localStorage.removeItem('token');
  }

  // CUSTOMERS METHODS
  async searchCustomers(query) {
    try {
      const customers = await this.request(
        `/customers/search?q=${encodeURIComponent(query)}`
      );

      // Cache dei risultati se online
      if (navigator.onLine && this.offlineStorage) {
        this.offlineStorage.cacheCustomers(customers);
      }

      return customers;
    } catch (error) {
      // Se offline, usa cache
      if (!navigator.onLine && this.offlineStorage) {
        const cached = this.offlineStorage.getCachedCustomers();
        return cached.filter(
          (customer) =>
            customer.companyName.toLowerCase().includes(query.toLowerCase()) ||
            customer.vatNumber?.includes(query)
        );
      }
      throw error;
    }
  }

  async createCustomer(customerData) {
    try {
      return await this.request('/customers', {
        method: 'POST',
        body: customerData,
      });
    } catch (error) {
      // Se offline, salva per sincronizzazione successiva
      if (!navigator.onLine && this.offlineStorage) {
        const offlineOperation =
          this.offlineStorage.saveOfflineCustomerOperation(
            'create',
            customerData
          );

        return {
          id: offlineOperation.id,
          ...customerData,
          isOffline: true,
          tempId: offlineOperation.id,
          createdAt: new Date().toISOString(),
        };
      }
      throw error;
    }
  }

  async updateCustomer(customerData) {
    try {
      // Per l'update, non includere isNewCustomer e non specificare ID
      const { isNewCustomer, ...updateData } = customerData;
      return await this.request('/customers', {
        method: 'POST',
        body: updateData,
      });
    } catch (error) {
      // Se offline, salva per sincronizzazione successiva
      if (!navigator.onLine && this.offlineStorage) {
        return {
          ...customerData,
          isOfflineModified: true,
          updatedAt: new Date().toISOString(),
        };
      }
      throw error;
    }
  }

  async deleteCustomer(id) {
    try {
      return await this.request(`/customers/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      // Se offline, salva per sincronizzazione successiva
      if (!navigator.onLine && this.offlineStorage) {
        return {
          success: true,
          message:
            'Cliente eliminato offline, sarà sincronizzato quando torni online',
          isOffline: true,
        };
      }
      throw error;
    }
  }

  async getCustomerById(id) {
    return this.request(`/customers/${id}`);
  }

  async getCustomers(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const customers = await this.request(`/customers?${params}`);

      // Cache automatica clienti solo se esplicitamente richiesto
      if (navigator.onLine && this.offlineStorage && filters.updateCache) {
        this.offlineStorage.cacheCustomers(customers);
      }

      return customers;
    } catch (error) {
      // Fallback cache offline
      if (!navigator.onLine && this.offlineStorage) {
        return this.offlineStorage.getCachedCustomers();
      }
      throw error;
    }
  }

  // Metodo specifico per preload di TUTTI i clienti
  async getAllCustomers() {
    try {
      // Usa parametri specifici per ottenere TUTTI i clienti
      const filters = {
        limit: 10000, // Limite molto alto
        page: 1,
      };

      const params = new URLSearchParams(filters);
      const response = await this.request(`/customers?${params}`);

      // Estrai i clienti dalla risposta - potrebbe essere response.data, response.customers, o response direttamente
      let customers;
      if (Array.isArray(response)) {
        customers = response;
      } else if (response && Array.isArray(response.data)) {
        customers = response.data;
      } else if (response && Array.isArray(response.customers)) {
        customers = response.customers;
      } else if (response && Array.isArray(response.results)) {
        customers = response.results;
      } else {
        console.warn(
          '⚠️ Struttura risposta clienti non riconosciuta:',
          response
        );
        customers = [];
      }

      // Cache automatica clienti
      if (navigator.onLine && this.offlineStorage && customers.length > 0) {
        this.offlineStorage.cacheCustomers(customers);
      }

      return customers;
    } catch (error) {
      console.error('❌ Errore nel preload di tutti i clienti:', error);

      // Fallback cache offline
      if (!navigator.onLine && this.offlineStorage) {
        return this.offlineStorage.getCachedCustomers();
      }
      throw error;
    }
  }

  // PRODUCTS METHODS
  async searchProducts(query) {
    try {
      const products = await this.request(
        `/products/search?q=${encodeURIComponent(query)}`
      );

      // Non fare cache automatica per le ricerche
      return products;
    } catch (error) {
      // Se offline, usa cache
      if (!navigator.onLine && this.offlineStorage) {
        const cached = this.offlineStorage.getCachedProducts();
        return cached.filter((product) => {
          const queryLower = query.toLowerCase();
          const code = (product.code || '').toLowerCase();
          const description = (product.description || '').toLowerCase();

          // Check exact substring match (original behavior)
          if (code.includes(queryLower) || description.includes(queryLower)) {
            return true;
          }

          // Check if any word starts with the query (partial word search)
          const words = description.split(/\s+/);
          return words.some((word) => word.startsWith(queryLower));
        });
      }
      throw error;
    }
  }

  async getProducts(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const products = await this.request(`/products?${params}`);

      // Cache automatica prodotti solo se esplicitamente richiesto
      if (navigator.onLine && this.offlineStorage && filters.updateCache) {
        this.offlineStorage.cacheProducts(products);
      }

      return products;
    } catch (error) {
      // Fallback cache offline
      if (!navigator.onLine && this.offlineStorage) {
        return this.offlineStorage.getCachedProducts();
      }
      throw error;
    }
  }

  // Metodo specifico per preload di TUTTI i prodotti
  async getAllProducts() {
    try {
      // Usa parametri specifici per ottenere TUTTI i prodotti
      const filters = {
        limit: 50000, // Limite molto alto per essere sicuri
        page: 1,
        // Rimosso active: true per prendere TUTTI i prodotti
      };

      const params = new URLSearchParams(filters);
      const products = await this.request(`/products?${params}`);

      // Cache automatica prodotti
      if (navigator.onLine && this.offlineStorage && products) {
        this.offlineStorage.cacheProducts(products);
      }

      return products;
    } catch (error) {
      // Fallback cache offline
      if (!navigator.onLine && this.offlineStorage) {
        return this.offlineStorage.getCachedProducts();
      }
      throw error;
    }
  }

  async getProductCategories() {
    return this.request('/products/categories');
  }

  // ORDERS METHODS
  async createOrder(orderData) {
    try {
      // Prova prima la richiesta normale
      return await this.request('/orders', {
        method: 'POST',
        body: orderData,
      });
    } catch (error) {
      // Se offline, salva per sincronizzazione successiva
      if (!navigator.onLine && this.offlineStorage) {
        const offlineOrder = this.offlineStorage.saveOfflineOrder(orderData);

        return {
          id: offlineOrder.id,
          orderNumber: `OFFLINE-${offlineOrder.id}`,
          ...orderData,
          status: 'pending_sync',
          createdAt: new Date(offlineOrder.timestamp).toISOString(),
          isOffline: true,
        };
      }
      throw error;
    }
  }

  async finalizeOrder(orderId) {
    // Non finalizzare ordini offline
    if (typeof orderId === 'string' && orderId.startsWith('offline_')) {
      return {
        message: 'Ordine verrà finalizzato durante la sincronizzazione',
      };
    }

    return this.request(`/orders/${orderId}/finalize`, {
      method: 'POST',
    });
  }

  async getOrders(filters = {}) {
    const params = new URLSearchParams(filters);

    // AGGIUNTO: Supporto per filtro agente
    if (filters.agentId && filters.agentId !== 'all') {
      params.set('agentId', filters.agentId);
    }

    return this.request(`/orders?${params}`);
  }

  async getOrderById(id) {
    return this.request(`/orders/${id}`);
  }

  async updateOrderStatus(id, status) {
    return this.request(`/orders/${id}/status`, {
      method: 'PUT',
      body: { status },
    });
  }

  async updateOrder(id, orderData) {
    return this.request(`/orders/${id}`, {
      method: 'PUT',
      body: orderData,
    });
  }

  async deleteOrder(id) {
    return this.request(`/orders/${id}`, {
      method: 'DELETE',
    });
  }

  // Metodo per scaricare PDF ordine
  async downloadOrderPDF(orderId) {
    try {
      const url = `${API_BASE}/orders/${orderId}/pdf/download`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore nel download del PDF');
      }

      // Ottieni il blob del PDF
      const blob = await response.blob();

      // Crea URL temporaneo per il download
      const downloadUrl = window.URL.createObjectURL(blob);

      // Crea elemento <a> temporaneo per il download
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Ottieni il nome file dall'header Content-Disposition se presente
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `ordine_${orderId}.pdf`;

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          contentDisposition
        );
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }

      link.download = fileName;

      // Aggiungi al DOM, clicca e rimuovi
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Pulisci l'URL temporaneo
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, fileName };
    } catch (error) {
      console.error('Errore download PDF:', error);
      throw error;
    }
  }

  async downloadOrderDDT(orderId) {
    try {
      const url = `${API_BASE}/orders/${orderId}/ddt/download`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore nel download del DDT');
      }

      // Ottieni il blob del PDF
      const blob = await response.blob();

      // Crea URL temporaneo per il download
      const downloadUrl = window.URL.createObjectURL(blob);

      // Crea elemento <a> temporaneo per il download
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Ottieni il nome file dall'header Content-Disposition se presente
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `ddt_${orderId}.pdf`;

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).+?\2|[^;\n]*)/.exec(
          contentDisposition
        );
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }

      link.download = fileName;

      // Aggiungi al DOM, clicca e rimuovi
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Pulisci l'URL temporaneo
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, fileName };
    } catch (error) {
      console.error('Errore download DDT:', error);
      throw error;
    }
  }

  // Metodo per visualizzare PDF ordine in una nuova tab
  async viewOrderPDF(orderId) {
    try {
      const url = `${API_BASE}/orders/${orderId}/pdf/view`;

      // Apri il PDF in una nuova finestra/tab
      const newWindow = window.open('', '_blank');

      if (!newWindow) {
        throw new Error(
          "Il browser ha bloccato l'apertura della finestra. Controlla le impostazioni popup."
        );
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        newWindow.close();
        throw new Error(error.error || 'Errore nella visualizzazione del PDF');
      }

      // Ottieni il blob del PDF
      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(blob);

      // Carica il PDF nella nuova finestra
      newWindow.location.href = pdfUrl;

      return { success: true, message: 'PDF aperto in una nuova finestra' };
    } catch (error) {
      console.error('Errore visualizzazione PDF:', error);
      throw error;
    }
  }

  async getOrderStats(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/orders/stats/summary?${params}`);
  }

  // SUPER ADMIN METHODS

  // Dashboard stats
  async getSuperAdminStats() {
    return this.request('/superadmin/dashboard/stats');
  }

  // Agents management
  async getAgents(page = 1, limit = 20) {
    const params = new URLSearchParams({ page, limit });
    return this.request(`/superadmin/agents?${params}`);
  }

  async createAgent(agentData) {
    return this.request('/superadmin/agents', {
      method: 'POST',
      body: agentData,
    });
  }

  async updateAgent(agentId, agentData) {
    return this.request(`/superadmin/agents/${agentId}`, {
      method: 'PUT',
      body: agentData,
    });
  }

  async resetAgentPassword(agentId) {
    return this.request(`/superadmin/agents/${agentId}/reset-password`, {
      method: 'POST',
    });
  }

  async deleteAgent(agentId) {
    return this.request(`/superadmin/agents/${agentId}`, {
      method: 'DELETE',
    });
  }

  // UTILITY METHODS

  // Verifica se un endpoint è disponibile offline
  isAvailableOffline(endpoint) {
    const offlineEndpoints = [
      '/customers/search',
      '/products/search',
      '/products',
      '/orders', // Solo per creazione
    ];

    return offlineEndpoints.some((pattern) => endpoint.includes(pattern));
  }

  // Status connessione
  isOnline() {
    return navigator.onLine;
  }

  // Configurazione token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken() {
    return this.token;
  }

  // Metodi helper per debugging
  getConnectionInfo() {
    return {
      isOnline: navigator.onLine,
      apiBase: API_BASE,
      hasToken: !!this.token,
      hasOfflineStorage: !!this.offlineStorage,
      environment: process.env.NODE_ENV,
      apiUrlSource: process.env.REACT_APP_API_URL
        ? 'env variable'
        : 'default localhost',
    };
  }
}

// Esporta istanza singleton
const apiServiceInstance = new ApiService();

// Log per verificare che il metodo esista
export default apiServiceInstance;
